const m = require('minthril');
const { component } = require('../../component');
const { Breadcrumbs } = require('../../components/breadcrumbs');
const { Pagination } = require('../../components/pagination');
const { loadAdminUsage } = require('../../controllers/admin');

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** index)).toFixed(index ? 2 : 0)} ${units[index]}`;
}

function formatDuration(milliseconds) {
  const seconds = milliseconds / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} hr`;
}

const AdminUsagePage = component((vnode, emit) => {
  let requestedUserId = null;
  let loading = false;
  let error = null;
  let usage = null;
  let pageTokens = [null];
  let pageIndex = 0;
  let requestVersion = 0;

  async function load(user, requestedPage = pageIndex) {
    const version = ++requestVersion;
    requestedUserId = user.uid;
    loading = true;
    error = null;
    emit('redraw');

    try {
      const result = await loadAdminUsage(user, pageTokens[requestedPage]);
      if (version !== requestVersion) return;
      usage = result;
      pageIndex = requestedPage;
      pageTokens = pageTokens.slice(0, pageIndex + 1);
      if (result.nextPageToken) pageTokens[pageIndex + 1] = result.nextPageToken;
    } catch (reason) {
      console.error('Unable to load administrator platform usage.', reason);
      if (version === requestVersion) error = reason.message;
    } finally {
      if (version === requestVersion) {
        loading = false;
        emit('redraw');
      }
    }
  }

  function changePage(nextPage) {
    if (loading || nextPage < 0 ||
      (nextPage > 0 && !pageTokens[nextPage])) return;
    load(userForPage, nextPage);
  }

  let userForPage = null;

  return {
    view: ({ attrs: { user } }) => {
      userForPage = user;
      if (!loading && requestedUserId !== user.uid) {
        pageTokens = [null];
        pageIndex = 0;
        load(user, 0);
      }

      const sites = [...(usage?.sites || [])].sort((left, right) => {
        const leftBytes = left.storage.cacheBytes + left.storage.screenshotBytes;
        const rightBytes = right.storage.cacheBytes + right.storage.screenshotBytes;
        return rightBytes - leftBytes;
      });

      return m('section.panel.admin-usage-page', [
        m(Breadcrumbs, { items: [
          { label: 'Admin', href: '/admin' },
          { label: 'Platform usage' }
        ] }),
        m('h2', 'Platform usage by site'),
        m('p.muted', usage
          ? `Page ${pageIndex + 1} · Usage for ${usage.period}. Storage shows the current objects in Cloud Storage; request and compute figures cover the month to date.`
          : 'Per-site storage and workload usage.'),
        error && m('p.error', error),
        loading && !usage && m('p.muted', 'Loading platform usage…'),
        usage && m('div.admin-usage-page__table-wrap',
          m('table.admin-usage-page__table', [
            m('thead', m('tr', [
              m('th', 'Site'),
              m('th', 'Owner ID'),
              m('th', 'Cache storage'),
              m('th', 'Screenshots'),
              m('th', 'Total stored'),
              m('th', 'Requests'),
              m('th', 'Response data'),
              m('th', 'Render time'),
              m('th', 'Validation time'),
              m('th', 'Normalized worker time')
            ])),
            m('tbody', sites.map(site => {
              const totalBytes = site.storage.cacheBytes + site.storage.screenshotBytes;
              return m('tr', [
                m('td', m('a', { href: `/admin/sites/${encodeURIComponent(site.id)}` }, site.name)),
                m('td', m('code', site.ownerId || '—')),
                m('td', `${formatBytes(site.storage.cacheBytes)} · ${site.storage.cacheObjects} objects`),
                m('td', `${formatBytes(site.storage.screenshotBytes)} · ${site.storage.screenshotObjects} objects`),
                m('td.admin-usage-page__total', formatBytes(totalBytes)),
                m('td', site.usage.requests.toLocaleString()),
                m('td', formatBytes(site.usage.responseBytes)),
                m('td', formatDuration(site.usage.renderDurationMs)),
                m('td', formatDuration(site.usage.validationDurationMs)),
                m('td', formatDuration(site.usage.workerComputeMs))
              ]);
            })),
            m('tfoot', m('tr', [
              m('th', 'Page subtotal'),
              m('td'),
              m('td', formatBytes(sites.reduce((total, site) => total + site.storage.cacheBytes, 0))),
              m('td', formatBytes(sites.reduce((total, site) => total + site.storage.screenshotBytes, 0))),
              m('td.admin-usage-page__total', formatBytes(sites.reduce((total, site) =>
                total + site.storage.cacheBytes + site.storage.screenshotBytes, 0))),
              m('td', sites.reduce((total, site) => total + site.usage.requests, 0).toLocaleString()),
              m('td', formatBytes(sites.reduce((total, site) => total + site.usage.responseBytes, 0))),
              m('td', formatDuration(sites.reduce((total, site) => total + site.usage.renderDurationMs, 0))),
              m('td', formatDuration(sites.reduce((total, site) => total + site.usage.validationDurationMs, 0))),
              m('td', formatDuration(sites.reduce((total, site) => total + site.usage.workerComputeMs, 0)))
            ]))
          ])
        ),
        usage && m('p.muted.admin-usage-page__note', 'This is measured product usage, not a direct allocation of Google Cloud charges. Shared networking, idle capacity, deployments, and platform overhead are not assigned to individual sites.'),
        usage && m(Pagination, {
          page: pageIndex,
          hasPrevious: pageIndex > 0,
          hasNext: Boolean(usage.nextPageToken),
          loading,
          itemCount: sites.length,
          label: 'sites',
          onChange: changePage
        })
      ]);
    }
  };
});

module.exports = { AdminUsagePage };
